<template>
  <div class="pps-root">

    <!-- ── Layout con resultados ── -->
    <div v-if="loading || sinResultados || cards.length" class="pps-body">

      <!-- Sidebar -->
      <aside class="pps-sidebar" v-if="categorias.length || !loading">

        <!-- Término buscado -->
        <div v-if="query && !loading" class="pps-search-term">
          <div class="pps-search-term-label">{{ query.replace(/-/g, ' ') }}</div>
          <div class="pps-search-term-count">{{ total }} resultado{{ total !== 1 ? 's' : '' }}</div>
        </div>
        <template v-if="categorias.length">
          <div class="pps-sidebar-heading">Categorías</div>
          <div
            v-for="cat in categorias"
            :key="cat.id"
            class="pps-sidebar-item"
            :class="{ 'pps-sidebar-item--active': categoriaActiva === cat.id }"
            @click="toggleCategoria(cat.id)"
          >
            <span class="pps-sidebar-label">{{ cat.name }}</span>
            <span class="pps-sidebar-count">{{ cat.count }}</span>
          </div>

          <div class="pps-sidebar-divider"></div>
        </template>

        <div class="pps-sidebar-heading">Precio</div>
        <div
          v-for="rango in rangosPrecio"
          :key="rango.label"
          class="pps-sidebar-item"
          :class="{ 'pps-sidebar-item--active': rangoActivo === rango.label }"
          @click="toggleRango(rango)"
        >
          <span class="pps-sidebar-label">{{ rango.label }}</span>
          <span class="pps-sidebar-count">{{ rango.count }}</span>
        </div>
      </aside>

      <!-- Main -->
      <div class="pps-main">

        <!-- Meta bar -->
        <div class="pps-meta" v-if="!loading && cardsFiltradas.length">
          <span class="pps-meta-count">{{ total }} resultado{{ total !== 1 ? 's' : '' }}</span>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="pps-grid">
          <div v-for="i in 12" :key="i" class="pps-card">
            <v-skeleton-loader type="image" class="pps-skeleton-img" />
            <div class="pps-card-body">
              <v-skeleton-loader type="text" class="mb-2" />
              <v-skeleton-loader type="text" width="60%" />
              <v-skeleton-loader type="text" width="40%" class="mt-2" />
            </div>
          </div>
        </div>

        <!-- Sin resultados -->
        <div v-else-if="sinResultados || (!loading && !cardsFiltradas.length)" class="pps-state">
          <v-icon size="52" color="grey darken-2">mdi-package-variant-remove</v-icon>
          <div class="grey--text text--lighten-1 mt-3">No se encontraron productos</div>
          <div class="grey--text caption mt-1">Probá con otro nombre o código</div>
        </div>

        <!-- Grid -->
        <div v-else>
          <div class="pps-grid">
            <div
              v-for="card in cardsFiltradas"
              :key="card.psp_id"
              class="pps-card"
              @click="$emit('card-click', card)"
            >
              <!-- Imagen -->
              <div class="pps-card-img-wrapper">
                <img
                  :src="card.imagen_principal || noImage"
                  :alt="card.descripcion"
                  class="pps-card-img"
                />
                <v-progress-circular
                  v-if="card.loading"
                  indeterminate
                  color="grey darken-1"
                  size="24"
                  width="2"
                  class="pps-card-img-loader"
                />
              </div>

              <!-- Info -->
              <div class="pps-card-body">
                <div class="pps-card-title">{{ card.descripcion }}</div>

                <div class="pps-card-price-area">
                  <v-skeleton-loader v-if="card.loading" type="text" width="90" />
                  <template v-else>
                    <div v-if="card.precio_venta" class="pps-card-price">
                      {{ card.precio_venta | convertPesoArg }}
                    </div>
                    <div v-else class="pps-card-noprice">Sin precio</div>
                  </template>
                </div>

                </div>
            </div>
          </div>

          <!-- Paginación -->
          <div v-if="totalPaginas > 1" class="pps-pagination">
            <v-pagination
              :value="page"
              :length="totalPaginas"
              :total-visible="7"
              @input="$emit('page-change', $event)"
            />
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import noImage from "@/assets/no-image.png";

export default {
  name: "PosProductoSearch",
  components: {},

  props: {
    cards: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    sinResultados: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 24 },
    query: { type: String, default: null },
  },

  data() {
    return {
      noImage,
      categoriaActiva: null,
      rangoActivo: null,
    };
  },

  computed: {
    categorias() {
      const map = {};
      this.cards.forEach(card => {
        const cat = card.categoria;
        if (!cat) return;
        if (!map[cat.id]) map[cat.id] = { id: cat.id, name: cat.name, count: 0 };
        map[cat.id].count++;
      });
      return Object.values(map).sort((a, b) => b.count - a.count);
    },

    rangosPrecio() {
      const precios = this.cards.map(c => c.precio_venta).filter(Boolean);
      if (!precios.length) return [];
      const max = Math.max(...precios);
      const rangos = [
        { label: `Hasta $${this.formatNum(max * 0.33)}`, min: 0, max: max * 0.33 },
        { label: `$${this.formatNum(max * 0.33)} a $${this.formatNum(max * 0.66)}`, min: max * 0.33, max: max * 0.66 },
        { label: `Más de $${this.formatNum(max * 0.66)}`, min: max * 0.66, max: Infinity },
      ];
      return rangos.map(r => ({
        ...r,
        count: this.cards.filter(c => c.precio_venta >= r.min && c.precio_venta <= r.max).length,
      })).filter(r => r.count > 0);
    },

    totalPaginas() {
      return Math.ceil(this.total / this.pageSize) || 1;
    },

    cardsFiltradas() {
      let result = this.cards;
      if (this.categoriaActiva) {
        result = result.filter(c => c.categoria?.id === this.categoriaActiva);
      }
      if (this.rangoActivo) {
        const rango = this.rangosPrecio.find(r => r.label === this.rangoActivo);
        if (rango) result = result.filter(c => c.precio_venta >= rango.min && c.precio_venta <= rango.max);
      }
      return result;
    },
  },

  watch: {
    cards() {
      this.categoriaActiva = null;
      this.rangoActivo = null;
    },
  },

  methods: {
    toggleCategoria(id) {
      this.categoriaActiva = this.categoriaActiva === id ? null : id;
    },
    toggleRango(rango) {
      this.rangoActivo = this.rangoActivo === rango.label ? null : rango.label;
    },
    formatNum(n) {
      return Math.round(n).toLocaleString("es-AR");
    },
  },
};
</script>

<style scoped>
/* ── Root ── */
.pps-root {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Body: sidebar + main ── */
.pps-body {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 0;
}

/* ── Sidebar ── */
.pps-sidebar {
  width: 200px;
  min-width: 200px;
  padding-right: 24px;
  padding-top: 4px;
}

/* ── Término buscado ── */
.pps-search-term {
  padding-bottom: 16px;
  margin-bottom: 4px;
  border-bottom: 1px solid #2a2a2a;
}

.pps-search-term-label {
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  text-transform: capitalize;
}

.pps-search-term-count {
  font-size: 0.78rem;
  color: #666;
  margin-top: 4px;
}

.pps-sidebar-heading {
  font-size: 0.8rem;
  font-weight: 700;
  color: #9e9e9e;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 10px;
  margin-top: 16px;
}

.pps-sidebar-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s ease;
  margin-bottom: 2px;
}
.pps-sidebar-item:hover {
  background: rgba(255, 255, 255, 0.06);
}
.pps-sidebar-item--active {
  background: rgba(52, 131, 250, 0.12);
}
.pps-sidebar-item--active .pps-sidebar-label {
  color: #3483fa;
  font-weight: 600;
}

.pps-sidebar-label {
  font-size: 0.83rem;
  color: #cccccc;
  line-height: 1.3;
}

.pps-sidebar-count {
  font-size: 0.75rem;
  color: #666;
  margin-left: 6px;
  flex-shrink: 0;
}

.pps-sidebar-divider {
  border-top: 1px solid #2e2e2e;
  margin: 16px 0 4px;
}

/* ── Main ── */
.pps-main {
  flex: 1;
  min-width: 0;
}

/* ── Meta bar ── */
.pps-meta {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #2a2a2a;
}
.pps-meta-count {
  font-size: 0.82rem;
  color: #888;
}

/* ── Estado vacío ── */
.pps-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  text-align: center;
}

.pps-skeleton-img {
  aspect-ratio: 1;
}

.pps-skeleton-img ::v-deep .v-skeleton-loader__image {
  height: 100% !important;
}

/* ── Grid ── */
.pps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1100px) {
  .pps-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 720px) {
  .pps-grid { grid-template-columns: repeat(2, 1fr); }
  .pps-sidebar { display: none; }
}

/* ── Card ── */
.pps-card {
  background: #1e1e1e;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.pps-card:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
  border-color: #3a3a3a;
}

/* Imagen */
.pps-card-img-wrapper {
  position: relative;
  background: #2a2a2a;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.pps-card-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px;
}
.pps-card-img-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Body */
.pps-card-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 12px;
  gap: 8px;
}

.pps-card-title {
  font-size: 0.82rem;
  color: #cccccc;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.pps-card-price-area {
  min-height: 28px;
}

.pps-card-price {
  font-size: 1.15rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.4px;
}

.pps-card-noprice {
  font-size: 0.78rem;
  color: #666;
}


/* ── Paginación ── */
.pps-pagination {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px;
}
</style>
